"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { useTranslations } from "next-intl";
import { SceneManager } from "./render/SceneManager";
import { addSnowLayer } from "./world/snowLayer";
import { renderInstanced, type PlacedInstance } from "./render/InstancedRenderer";
import { animateMaterialize, animatePortalFlood, portalCentroid } from "./render/buildAnimation";
import { PORTAL_FLOOD_MS, PORTAL_DRAIN_MS } from "./config";
import { frameScene } from "./render/FrameUtils";
import { buildWorld } from "@/app/components/terrain/world/buildWorld";
import { buildNether } from "@/app/components/terrain/world/buildNether";
import { BIOME, BLOCK, type Biome, type BiomeMap } from "./types";

type Dimension = "overworld" | "nether";

const PORTAL_WOBBLE: Keyframe[] = [
    { transform: "scale(1) skew(0deg, 0deg)", filter: "blur(0px)" },
    {
        transform: "scale(1.05) skew(1.6deg, -1deg)",
        filter: "blur(1.6px) hue-rotate(-30deg) saturate(1.35)",
        offset: 0.2,
    },
    {
        transform: "scale(1.02) skew(-1.4deg, 0.9deg)",
        filter: "blur(1.2px) hue-rotate(20deg) saturate(1.2)",
        offset: 0.45,
    },
    {
        transform: "scale(1.04) skew(0.8deg, -0.6deg)",
        filter: "blur(0.8px) hue-rotate(-12deg)",
        offset: 0.7,
    },
    { transform: "scale(1) skew(0deg, 0deg)", filter: "blur(0px)" },
];

const WOBBLE_TOTAL_MS = PORTAL_FLOOD_MS + PORTAL_DRAIN_MS;

const ALL_BIOMES: readonly Biome[] = Object.values(BIOME);

export interface TerrainBannerProps {
    className?: string;
    showHint?: boolean;
    title?: string;
    ariaLabel?: string;
    view?: "isometric";
    debug?: boolean;
}

function readHash(): { seed: number | null; dim: Dimension } {
    if (typeof window === "undefined") return { seed: null, dim: "overworld" };
    const m = window.location.hash.match(/seed=(\d+)/);
    const dim: Dimension = window.location.hash.includes("dim=nether") ? "nether" : "overworld";
    return { seed: m ? Number(m[1]) : null, dim };
}

function writeHash(seed: number, dim: Dimension): void {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.hash = dim === "nether" ? `seed=${seed}&dim=nether` : `seed=${seed}`;
    window.history.replaceState(null, "", url.toString());
    window.dispatchEvent(new HashChangeEvent("hashchange"));
}

export default function TerrainBanner({
    className = "",
    showHint = false,
    title,
    ariaLabel,
    debug = false,
}: TerrainBannerProps) {
    const t = useTranslations();
    const containerRef = useRef<HTMLDivElement>(null);
    const [seed, setSeed] = useState<number | null>(null);
    const [dim, setDim] = useState<Dimension>("overworld");
    const [forcedBiome, setForcedBiome] = useState<Biome | undefined>(undefined);
    const clickCtxRef = useRef<{
        scene: SceneManager;
        targets: THREE.Object3D[];
        placed: PlacedInstance[];
    } | null>(null);
    const crossingRef = useRef<"flooding" | "flooded" | null>(null);
    const floodCancelRef = useRef<(() => void) | null>(null);
    const drainCancelRef = useRef<(() => void) | null>(null);
    const [hintVisible, setHintVisible] = useState<boolean>(showHint);
    const [themeTick, setThemeTick] = useState(0);
    const hasMountedRef = useRef(false);
    const sceneRef = useRef<SceneManager | null>(null);

    useEffect(() => {
        if (typeof document === "undefined") return;
        const el = document.documentElement;
        const observer = new MutationObserver(() => setThemeTick((n) => n + 1));
        observer.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
        return () => observer.disconnect();
    }, []);

    // Client-only initial seed: URL hash override or random. Documented React
    // pattern for SSR-safe randomness.
    useEffect(() => {
        const h = readHash();
        const initial = h.seed ?? Math.floor(Math.random() * 100_000_000);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSeed(initial);
        setDim(h.dim);
        if (h.seed === null) writeHash(initial, h.dim);

        const onHash = () => {
            const next = readHash();
            if (next.seed !== null) setSeed(next.seed);
            setDim(next.dim);
        };
        window.addEventListener("hashchange", onHash);
        return () => window.removeEventListener("hashchange", onHash);
    }, []);

    const built = useMemo(() => {
        if (seed == null) return null;
        if (dim === "nether") {
            const { world, heights } = buildNether(seed);
            return { world, heights, biomes: null as BiomeMap | null };
        }
        const b = buildWorld(seed, { forceBiome: forcedBiome });
        return { world: b.world, heights: b.heights, biomes: b.biomes as BiomeMap | null };
    }, [seed, dim, forcedBiome]);

    useEffect(() => {
        if (!containerRef.current) return;
        const scene = new SceneManager(containerRef.current);
        sceneRef.current = scene;
        return () => {
            sceneRef.current = null;
            scene.dispose();
        };
    }, []);

    useEffect(() => {
        const scene = sceneRef.current;
        if (!scene) return;
        scene.applyTheme();
        scene.render();
    }, [themeTick]);

    useEffect(() => {
        const scene = sceneRef.current;
        if (!built || !scene || !containerRef.current) return;
        const el = containerRef.current;

        scene.clearWorld();
        const placed = renderInstanced(scene, built.world);
        if (built.biomes) {
            placed.push(...addSnowLayer(scene, built.world, built.heights, built.biomes));
        }
        frameScene(scene);

        const portalMats: THREE.Material[] = [
            scene.materials[BLOCK.Portal].material,
            scene.materials[BLOCK.Obsidian].material,
        ];
        clickCtxRef.current = {
            scene,
            targets: scene.group.children.filter((c) => {
                const mesh = c as THREE.InstancedMesh;
                return mesh.isInstancedMesh && portalMats.includes(mesh.material as THREE.Material);
            }),
            placed,
        };

        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const arriving = crossingRef.current === "flooded";
        crossingRef.current = null;

        let anim: Animation | null = null;
        let cancelMaterialize: (() => void) | null = null;
        if (arriving) {
            const origin = portalCentroid(built.world) ?? new THREE.Vector3(0, 12, 0);
            drainCancelRef.current = animatePortalFlood(scene, placed, origin, "drain");
        } else if (!reduced) {
            if (!hasMountedRef.current) {
                anim = el.animate(
                    [
                        { opacity: 0, transform: "scale(0.96)" },
                        { opacity: 1, transform: "scale(1)" },
                    ],
                    { duration: 700, easing: "ease-out", fill: "both" },
                );
            }
            // Ring of light sweeping outward, brightening blocks into their true
            // color as it passes. Recolors already-placed instances only, so it
            // can't expose the face-culling gaps a moved instance would.
            cancelMaterialize = animateMaterialize(scene, placed);
        }
        hasMountedRef.current = true;

        return () => {
            if (floodCancelRef.current) {
                floodCancelRef.current();
                floodCancelRef.current = null;
                if (crossingRef.current === "flooding") crossingRef.current = null;
            }
            clickCtxRef.current = null;
            cancelMaterialize?.();
            drainCancelRef.current?.();
            drainCancelRef.current = null;
            anim?.cancel();
        };
    }, [built]);

    const crossTo = (target: Dimension) => {
        if (seed == null || crossingRef.current) return;
        const ctx = clickCtxRef.current;
        const world = built?.world;
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduced || !ctx || !world) {
            writeHash(seed, target);
            setDim(target);
            return;
        }

        drainCancelRef.current?.();
        drainCancelRef.current = null;

        crossingRef.current = "flooding";
        containerRef.current
            ?.querySelector("canvas")
            ?.animate(PORTAL_WOBBLE, { duration: WOBBLE_TOTAL_MS, easing: "ease-in-out" });

        const origin = portalCentroid(world) ?? new THREE.Vector3(0, 12, 0);
        floodCancelRef.current = animatePortalFlood(ctx.scene, ctx.placed, origin, "flood", () => {
            floodCancelRef.current = null;
            crossingRef.current = "flooded";
            writeHash(seed, target);
            setDim(target);
        });
    };

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (seed == null || crossingRef.current) return;

        if (dim === "nether") {
            crossTo("overworld");
            return;
        }

        const ctx = clickCtxRef.current;
        const el = containerRef.current;
        if (ctx && el && ctx.targets.length > 0) {
            const rect = el.getBoundingClientRect();
            const ndc = new THREE.Vector2(
                ((e.clientX - rect.left) / rect.width) * 2 - 1,
                -(((e.clientY - rect.top) / rect.height) * 2 - 1),
            );
            const raycaster = new THREE.Raycaster();
            raycaster.setFromCamera(ndc, ctx.scene.camera);
            if (raycaster.intersectObjects(ctx.targets, false).length > 0) {
                if (hintVisible) setHintVisible(false);
                crossTo("nether");
                return;
            }
        }

        const next = Math.floor(Math.random() * 100_000_000);
        writeHash(next, "overworld");
        setSeed(next);
        if (hintVisible) setHintVisible(false);
    };

    const biomeName = built?.biomes?.biome[0][0] ?? (dim === "nether" ? "nether" : "");

    return (
        <div
            ref={containerRef}
            className={`terrain-banner relative overflow-hidden ${className}`.trim()}
            onClick={handleClick}
            role="img"
            aria-label={ariaLabel ?? title ?? t("home.terrainAria")}
            title={title}
        >
            {showHint && hintVisible && (
                <p className="label--dragInstruct">{t("terrain.clickToRegenerate")}</p>
            )}

            {debug && (
                <>
                    <div className="pointer-events-none absolute left-1 top-1 rounded bg-black/50 px-2 py-1 text-[10px] leading-none text-white">
                        <div>seed: {seed ?? "–"}</div>
                        <div>biome: {seed == null ? "–" : biomeName}</div>
                    </div>
                    <div className="absolute right-1 top-1 flex flex-col gap-1 rounded bg-black/50 p-1 text-[10px] text-white">
                        <div className="mb-1 opacity-80">force biome</div>
                        <div className="flex flex-wrap gap-1">
                            {ALL_BIOMES.map((b) => (
                                <button
                                    key={b}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setForcedBiome((prev) => (prev === b ? undefined : b));
                                    }}
                                    className={`rounded px-2 py-1 ${forcedBiome === b ? "bg-white/80 text-black" : "bg-white/20"}`}
                                >
                                    {b}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setForcedBiome(undefined);
                            }}
                            className="mt-1 rounded bg-white/20 px-2 py-1"
                        >
                            clear
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
